import { useEffect, useMemo, useState } from 'react';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { ExpandableSearch } from '@components/expandable-search';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconBuilder } from '@components/icons/builder';
import { IconInfoSimple } from '@components/icons/info-simple';
import { IconNotifications } from '@components/icons/notifications';
import { IconNotificationsOff } from '@components/icons/notifications-off';
import { IconSettings } from '@components/icons/settings';
import { IconStatistics } from '@components/icons/statistics';
import { IconTraveller } from '@components/icons/traveller';
import { IconWorkspace } from '@components/icons/workspace';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { useTableRef } from '@components/table/table';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import {
  useJourneyNotification,
  useSubscribeJourneyNotification,
  useUnsubscribeJourneyNotification,
} from 'api/journey-notifications/hooks';
import { JourneyNotificationType } from 'api/journey-notifications/requests';
import { useJourney, useSaveJourneyTitle } from 'api/journeys/hooks';
import { JourneyType, TravellerJourneyListItem } from 'api/types';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

import { JourneyMenu } from './common';
import { JourneyBreadcrumb } from './journey-breadcrumb';
import { useJourneyDetailsSidebar } from './journey-edit-sidebar';
import { JourneyFields } from './journey-fields';
import { JourneySettings } from './journey-settings';
import { OverviewTab } from './overview';
import { SelectionHeader } from './selection-header';
import { t } from './traveller-journey-list/locale';
import {
  AddTravellerButton,
  TravellerJourneysTable,
} from './traveller-journey-list/traveller-journeys-table';

import { useCurrentWorkspace } from '../../helpers';

const JourneyNotificationsMenu = ({ journeyShortId }: { journeyShortId: string }) => {
  const errorQuery = useJourneyNotification(journeyShortId, 'error');
  const completionQuery = useJourneyNotification(journeyShortId, 'completion');
  const errorSubscribe = useSubscribeJourneyNotification(journeyShortId, 'error');
  const errorUnsubscribe = useUnsubscribeJourneyNotification(journeyShortId, 'error');
  const completionSubscribe = useSubscribeJourneyNotification(journeyShortId, 'completion');
  const completionUnsubscribe = useUnsubscribeJourneyNotification(journeyShortId, 'completion');

  const items: {
    type: JourneyNotificationType;
    label: string;
    query: typeof errorQuery;
    subscribe: typeof errorSubscribe;
    unsubscribe: typeof errorUnsubscribe;
  }[] = [
    {
      type: 'error',
      label: t('notifyOnError'),
      query: errorQuery,
      subscribe: errorSubscribe,
      unsubscribe: errorUnsubscribe,
    },
    {
      type: 'completion',
      label: t('notifyOnCompletion'),
      query: completionQuery,
      subscribe: completionSubscribe,
      unsubscribe: completionUnsubscribe,
    },
  ];

  const isLoading = items.some((item) => item.query.isLoading);
  const isMutating = items.some((item) => item.subscribe.isLoading || item.unsubscribe.isLoading);
  const enabledCount = items.reduce((acc, item) => acc + (item.query.data?.subscribed ? 1 : 0), 0);
  const disabled = isLoading || isMutating;

  return (
    <Menu
      buttonProps={{
        variant: 'secondary',
        text: '',
        iconLeft: (
          <Stack space={1} orientation="horizontal" align="center">
            {enabledCount > 0 ? (
              <>
                <IconNotifications />
                <Badge size="md" text={enabledCount} />
              </>
            ) : (
              <IconNotificationsOff />
            )}
          </Stack>
        ),
        disabled,
      }}
    >
      <NotificationsHeader>
        <Stack space={0} orientation="vertical">
          <Text font="semibold14" color="textPrimary" text={t('notifications')} />
          <Text font="regular14" color="textHelper" text={t('notifyDescription')} />
        </Stack>
      </NotificationsHeader>
      {items.map((item) => (
        <Menu.Switch
          size="sm"
          key={item.type}
          label={item.label}
          value={item.query.data?.subscribed ?? false}
          onChange={(nextValue) =>
            nextValue ? item.subscribe.mutate() : item.unsubscribe.mutate()
          }
        />
      ))}
    </Menu>
  );
};

const JourneyDetailActions = ({
  journey,
  onSearchChange,
  showDetailSidebar,
}: {
  journey: JourneyType;
  onSearchChange(search: string): void;
  showDetailSidebar: (props: { journey: JourneyType }) => void;
}) => {
  const navigate = useNavigate();

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={onSearchChange} />
      <Button
        variant="secondary"
        text={t('openJourneyBuilder')}
        iconLeft={<IconBuilder />}
        onClick={() =>
          navigate(
            generatePath(routePaths.editJourney, {
              workspaceId: journey.workspace,
              journeyId: journey.shortId,
              '*': '',
            })
          )
        }
      />
      <Button
        id="journey-details"
        variant="secondary"
        text={t('details')}
        iconLeft={<IconInfoSimple />}
        onClick={() => showDetailSidebar({ journey })}
      />
      <JourneyNotificationsMenu journeyShortId={journey.shortId} />
      <AddTravellerButton journey={journey} />
      <JourneyMenu journey={journey} />
    </Stack>
  );
};

export const JourneyDetails = () => {
  const [searchStr, setSearchStr] = useState('');
  const { isEnabled: hasTimeSavedEnabled } = useFeature('FEATURE_TIME_SAVED');
  const params = useParams();
  const journeyId = params.journeyId ?? '';
  const journey = useJourney(journeyId);
  const [selectedTravellers, setSelectedTravellers] = useState<Array<TravellerJourneyListItem>>([]);
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const showDetailSidebar = useJourneyDetailsSidebar();
  const { isAdmin, isEditor } = usePermissions();
  const canRenameJourney = isAdmin || isEditor;
  const saveJourneyTitle = useSaveJourneyTitle(journey.data);
  const attributes: Array<AttributeType | TravellerAttributeTemplate> = useMemo(() => {
    const companyTravellerAttributes = companyTravellerAttributeTemplatesQuery.data;
    return [
      ...(companyTravellerAttributes ?? []).map((attribute) => ({
        ...attribute,
        isTravellerAttribute: true,
      })),
      ...(journey.data?.attributes ?? []),
    ];
  }, [companyTravellerAttributeTemplatesQuery.data, journey.data?.attributes]);

  const tableRef = useTableRef();
  const workspace = useCurrentWorkspace();
  const WorkspaceIcon = ICON_CHOOSER_ICONS[workspace.icon] || IconWorkspace;

  useEffect(() => {
    setSelectedTravellers([]);
    tableRef.current?.clearSelected?.();
  }, [journeyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (journey.isLoading) {
    return <Spinner />;
  }

  if (journey.isError) {
    return <Navigate to={routePaths.travellersList} />;
  }

  const tabs = [
    {
      label: t('travellers'),
      section: 'travellers',
      icon: <IconTraveller />,
      content: (
        <TravellerJourneysTable
          key={journeyId}
          searchStr={searchStr}
          setSelectedTravellers={setSelectedTravellers}
          journey={journey.data}
          tableRef={tableRef}
          attributes={attributes}
        />
      ),
    },
    {
      label: t('fields'),
      section: 'attributes',
      icon: <IconAttributeJourney />,
      content: <JourneyFields attributes={journey.data.attributes} journeyShortId={journeyId} />,
    },
    {
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
      content: (
        <JourneySettings
          journeyTimezone={journey.data.timezone as string}
          notifyManualActionAssignees={journey.data.notifyManualActionAssignees ?? true}
        />
      ),
    },
    ...(hasTimeSavedEnabled
      ? [
          {
            label: t('overview'),
            section: 'overview',
            icon: <IconStatistics />,
            content: <OverviewTab journeyShortId={journeyId} />,
          },
        ]
      : []),
  ];

  return (
    <RouterTabsHeader
      actions={
        <JourneyDetailActions
          onSearchChange={setSearchStr}
          journey={journey.data}
          showDetailSidebar={showDetailSidebar}
        />
      }
      showSelectionHeader={selectedTravellers.length > 0}
      selectionHeader={
        <SelectionHeader
          clearSelectedTravellers={() => tableRef.current?.clearSelected()}
          selectedTravellers={selectedTravellers}
          journeyId={journeyId}
        />
      }
      breadcrumbs={
        <JourneyBreadcrumb
          journey={journey.data}
          workspaceId={journey.data.workspace}
          onRename={canRenameJourney ? saveJourneyTitle : undefined}
          showEditSidebar={() => showDetailSidebar({ journey: journey.data, isEditing: true })}
        />
      }
      tabs={tabs}
      backAction={
        <IconContainer
          hasBorder
          icon={<WorkspaceIcon color={workspace.color} />}
          variant={COLOR_TO_VARIANT[workspace.color]}
        />
      }
    />
  );
};

const NotificationsHeader = styled.div({
  padding: theme.space[2],
});

const IconContainer = styled(IconFancyContainer)({
  borderWidth: pxToRem(1),
});
